import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Search, Send, Heart, Star, Eye, Trash2, Trophy, Sparkles, MessageSquare } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MOTIVATION_TEMPLATES = [
  { icon: '⭐', text: 'أحسنت! استمر بالعطاء، أداؤك متميز ونفتخر بك.' },
  { icon: '🔥', text: 'إنجازك هذا الأسبوع رائع! حافظ على نفس الزخم.' },
  { icon: '🚀', text: 'أنت قادر على تحقيق المزيد، ثق بقدراتك واستمر.' },
  { icon: '💪', text: 'كل جهد يبذل اليوم هو خطوة نحو نجاحك غداً.' },
];
const THANKS_TEMPLATES = [
  { icon: '🙏', text: 'شكراً لجهودك وتفانيك في العمل، نقدر ذلك حق التقدير.' },
  { icon: '🏆', text: 'أحسنت! تستحق الشكر على إنجازاتك المميزة.' },
  { icon: '💝', text: 'تقديراً لجهودك المباركة، شكراً من القلب.' },
];

export default function SuperMessages() {
  const [searchQuery, setSearchQuery] = useState('');
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [messageType, setMessageType] = useState('motivation');
  const [messageText, setMessageText] = useState('');
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteCandidate, setDeleteCandidate] = useState(null); // {id, employee_name}
  const [deleting, setDeleting] = useState(false);

  const loadEmployees = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/super/employees/search`, { params: { q: searchQuery } });
      setEmployees(r.data || []);
    } catch (e) {
      toast.error('تعذر جلب الموظفين');
    }
  }, [searchQuery]);

  const loadHistory = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/super/employee-messages`);
      setHistory(r.data.items || []);
    } catch (e) { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    const t = setTimeout(loadEmployees, 200);
    return () => clearTimeout(t);
  }, [loadEmployees]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const send = async () => {
    if (!selectedEmployee) { toast.error('اختر الموظف أولاً'); return; }
    if (!messageText.trim()) { toast.error('اكتب نص الرسالة'); return; }
    setSending(true);
    try {
      await axios.post(`${API_URL}/api/super/employee-messages`, {
        employee_id: selectedEmployee.id,
        message: messageText.trim(),
        message_type: messageType,
      });
      toast.success(`تم إرسال الرسالة إلى ${selectedEmployee.name} 🎉`, { duration: 4000 });
      setMessageText('');
      setSelectedEmployee(null);
      setSearchQuery('');
      loadHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'فشل الإرسال');
    } finally { setSending(false); }
  };

  const confirmDelete = async () => {
    if (!deleteCandidate) return;
    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/api/super/employee-messages/${deleteCandidate.id}`);
      toast.success('تم الحذف');
      setDeleteCandidate(null);
      loadHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'فشل الحذف');
    } finally {
      setDeleting(false);
    }
  };

  const templates = messageType === 'motivation' ? MOTIVATION_TEMPLATES : THANKS_TEMPLATES;

  return (
    <div className="space-y-4 max-w-5xl mx-auto" data-testid="super-messages-page">
      <div className="flex items-center gap-2">
        <Sparkles className="h-6 w-6 text-primary" />
        <h1 className="text-xl sm:text-2xl font-black">رسائل التحفيز والشكر</h1>
      </div>
      <p className="text-sm text-foreground/60">أرسل رسالة تحفيزية أو شكر لأي موظف. سيراها فقط هو عند تسجيل دخوله.</p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Compose */}
        <Card className="p-4 space-y-3">
          <h3 className="font-bold flex items-center gap-2 text-sm"><Send className="h-4 w-4 text-primary" />إنشاء رسالة جديدة</h3>

          {/* Employee picker */}
          <div className="space-y-1.5">
            <Label className="text-xs">الموظف</Label>
            {selectedEmployee ? (
              <div className="flex items-center gap-2 p-2 rounded-md bg-primary/10 border border-primary/30">
                <div className="flex-1">
                  <p className="font-bold text-sm">{selectedEmployee.name}</p>
                  <p className="text-[10px] text-foreground/55">{selectedEmployee.branch_name}</p>
                </div>
                <Button type="button" variant="ghost" size="sm" onClick={() => setSelectedEmployee(null)} className="h-7 text-xs" data-testid="clear-employee-btn">
                  تغيير
                </Button>
              </div>
            ) : (
              <>
                <div className="relative">
                  <Search className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-foreground/40 pointer-events-none" />
                  <Input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="ابحث باسم الموظف..."
                    className="pr-8 h-9 text-sm"
                    data-testid="search-employee-input"
                  />
                </div>
                {employees.length > 0 && (
                  <div className="max-h-48 overflow-y-auto rounded-md border border-border bg-background">
                    {employees.map((e) => (
                      <button
                        key={e.id}
                        type="button"
                        onClick={() => { setSelectedEmployee(e); setSearchQuery(''); }}
                        className="w-full flex items-center gap-2 p-2 hover:bg-accent/60 text-right border-b border-border/40 last:border-0"
                        data-testid={`select-employee-${e.id}`}
                      >
                        <div className="flex-1">
                          <p className="text-sm font-bold">{e.name}</p>
                          <p className="text-[10px] text-foreground/55">{e.branch_name}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Type selector */}
          <div className="space-y-1.5">
            <Label className="text-xs">نوع الرسالة</Label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setMessageType('motivation')}
                className={`flex items-center justify-center gap-1.5 px-3 py-2 rounded-md font-bold text-sm transition-colors ${messageType === 'motivation' ? 'bg-amber-500 text-white' : 'bg-muted text-foreground/70 hover:bg-accent'}`}
                data-testid="type-motivation"
              >
                <Star className="h-4 w-4" />
                رسالة تحفيزية
              </button>
              <button
                type="button"
                onClick={() => setMessageType('thanks')}
                className={`flex items-center justify-center gap-1.5 px-3 py-2 rounded-md font-bold text-sm transition-colors ${messageType === 'thanks' ? 'bg-emerald-600 text-white' : 'bg-muted text-foreground/70 hover:bg-accent'}`}
                data-testid="type-thanks"
              >
                <Heart className="h-4 w-4" />
                رسالة شكر
              </button>
            </div>
          </div>

          {/* Templates */}
          <div className="space-y-1.5">
            <Label className="text-xs">قوالب جاهزة (انقر للاستخدام)</Label>
            <div className="space-y-1.5">
              {templates.map((t, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setMessageText(t.text)}
                  className="w-full flex items-start gap-2 p-2 rounded-md bg-accent/30 hover:bg-accent/60 transition-colors text-right text-xs"
                  data-testid={`template-${i}`}
                >
                  <span className="text-lg shrink-0">{t.icon}</span>
                  <span className="flex-1">{t.text}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Message text */}
          <div className="space-y-1.5">
            <Label className="text-xs">نص الرسالة</Label>
            <Textarea
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
              placeholder="اكتب رسالتك هنا..."
              rows={4}
              className="text-sm resize-none"
              maxLength={500}
              data-testid="message-text"
            />
            <p className="text-[10px] text-foreground/50 text-left">{messageText.length}/500</p>
          </div>

          <Button
            onClick={send}
            disabled={sending || !selectedEmployee || !messageText.trim()}
            className="w-full h-10 gap-2"
            data-testid="send-message-btn"
          >
            <Send className="h-4 w-4" />
            {sending ? 'جاري الإرسال...' : 'إرسال'}
          </Button>
        </Card>

        {/* History */}
        <Card className="p-4 space-y-3">
          <h3 className="font-bold flex items-center gap-2 text-sm"><MessageSquare className="h-4 w-4 text-primary" />سجل الرسائل المرسلة ({history.length})</h3>
          {loading ? (
            <p className="text-xs text-foreground/50 text-center py-8">جاري التحميل...</p>
          ) : history.length === 0 ? (
            <div className="text-center py-8 text-foreground/50">
              <Trophy className="h-12 w-12 mx-auto mb-2 opacity-30" />
              <p className="text-sm">لا توجد رسائل مرسلة بعد</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-[500px] overflow-y-auto">
              {history.map((m) => (
                <div key={m.id} className={`p-2.5 rounded-md border ${m.message_type === 'motivation' ? 'border-amber-200 bg-amber-50' : 'border-emerald-200 bg-emerald-50'}`} data-testid={`history-msg-${m.id}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${m.message_type === 'motivation' ? 'bg-amber-500 text-white' : 'bg-emerald-600 text-white'}`}>
                      {m.message_type === 'motivation' ? '⭐ تحفيز' : '🙏 شكر'}
                    </span>
                    <span className="text-xs font-bold flex-1 truncate">{m.employee_name}</span>
                    {m.read_at && <Eye className="h-3.5 w-3.5 text-emerald-600" title="مقروءة" data-testid={`read-eye-${m.id}`} />}
                    <button
                      type="button"
                      onClick={() => setDeleteCandidate({ id: m.id, employee_name: m.employee_name })}
                      className="text-destructive hover:bg-destructive/10 rounded p-1 transition-colors"
                      data-testid={`delete-msg-${m.id}`}
                      aria-label="حذف الرسالة"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                  <p className="text-xs leading-relaxed mb-1">{m.message}</p>
                  <div className="flex items-center gap-2 text-[10px] text-foreground/50">
                    <span className="font-numbers">{new Date(m.sent_at).toLocaleString('ar-EG', { dateStyle: 'short', timeStyle: 'short' })}</span>
                    {m.read_at && <span>· قُرئت {new Date(m.read_at).toLocaleString('ar-EG', { dateStyle: 'short', timeStyle: 'short' })}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Delete confirmation dialog */}
      <AlertDialog open={!!deleteCandidate} onOpenChange={(o) => { if (!o) setDeleteCandidate(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>حذف الرسالة؟</AlertDialogTitle>
            <AlertDialogDescription>
              سيتم حذف الرسالة المرسلة إلى <strong>{deleteCandidate?.employee_name}</strong> من السجل بشكل نهائي. لا يمكن التراجع عن هذه الخطوة.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-btn">إلغاء</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={deleting}
              className="bg-destructive text-white hover:bg-destructive/90"
              data-testid="delete-confirm-btn"
            >
              {deleting ? 'جاري الحذف...' : 'حذف نهائياً'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
